import React from 'react';
import { useTranslation } from 'react-i18next';
import type { GoogleReviewsComponent } from '../../../types/builder';
import { useBuilderStore } from '../../../store/builderStore';
import { ColorPicker } from '../../Common/ColorPicker';
import { ToggleSwitch } from '../../Common/ToggleSwitch';
import { ImageUploader } from '../../Common/ImageUploader';
import { Plus, Trash2 } from 'lucide-react';

interface GoogleReviewsSettingsProps {
  component: GoogleReviewsComponent;
}

export const GoogleReviewsSettings: React.FC<GoogleReviewsSettingsProps> = ({ component }) => {
  const { t, i18n } = useTranslation('builder');
  const { updateComponent } = useBuilderStore();

  const handleUpdate = (updates: Partial<GoogleReviewsComponent['settings']>) => {
    updateComponent(component.id, {
      settings: { ...component.settings, ...updates },
    });
  };

  const addReview = () => {
    const localeTag = i18n.language?.startsWith('th') ? 'th-TH' : undefined;
    const newReview = {
      id: `review-${Date.now()}`,
      authorName: t('settings.googleReviews.newReviewerName'),
      authorImage: '',
      rating: 5,
      reviewText: t('settings.googleReviews.newReviewText'),
      date: new Date().toLocaleDateString(localeTag, { year: 'numeric', month: 'short', day: 'numeric' }),
    };
    handleUpdate({ reviews: [...component.settings.reviews, newReview] });
  };

  const removeReview = (reviewId: string) => {
    if (component.settings.reviews.length <= 1) {
      alert(t('settings.googleReviews.atLeastOne'));
      return;
    }
    handleUpdate({
      reviews: component.settings.reviews.filter((r) => r.id !== reviewId),
    });
  };

  const updateReview = (reviewId: string, updates: any) => {
    handleUpdate({
      reviews: component.settings.reviews.map((r) =>
        r.id === reviewId ? { ...r, ...updates } : r
      ),
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.title')}</label>
        <input
          type="text"
          value={component.settings.title}
          onChange={(e) => handleUpdate({ title: e.target.value })}
          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder={t('settings.googleReviews.titlePlaceholder')}
        />
      </div>

      {/* Reviews */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="block text-sm font-medium">{t('settings.googleReviews.reviews')}</label>
          <button
            onClick={addReview}
            className="flex items-center gap-1 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
          >
            <Plus size={16} />
            {t('settings.googleReviews.addReview')}
          </button>
        </div>

        <div className="space-y-4">
          {component.settings.reviews.map((review, index) => (
            <div
              key={review.id}
              className="border border-gray-300 rounded-lg p-4 bg-gray-50"
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-700">
                  {t('settings.googleReviews.reviewNumber', { n: index + 1 })}
                </span>
                {component.settings.reviews.length > 1 && (
                  <button
                    onClick={() => removeReview(review.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">
                    {t('settings.googleReviews.reviewerPhoto')}
                  </label>
                  <ImageUploader
                    value={review.authorImage || ''}
                    onChange={(url) => updateReview(review.id, { authorImage: url })}
                    aspectRatio="avatar"
                  />
                </div>

                <div>
                  <label className="block text-xs text-gray-600 mb-1">{t('settings.googleReviews.reviewerName')}</label>
                  <input
                    type="text"
                    value={review.authorName}
                    onChange={(e) =>
                      updateReview(review.id, { authorName: e.target.value })
                    }
                    placeholder={t('settings.googleReviews.reviewerNamePlaceholder')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
                  />
                </div>

                <div>
                  <label className="block text-xs text-gray-600 mb-1">
                    {t('settings.googleReviews.ratingLabel', { value: review.rating })}
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    value={review.rating}
                    onChange={(e) =>
                      updateReview(review.id, { rating: parseInt(e.target.value) })
                    }
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>1★</span>
                    <span>5★</span>
                  </div>
                </div>

                <div>
                  <label className="block text-xs text-gray-600 mb-1">{t('settings.googleReviews.reviewText')}</label>
                  <textarea
                    value={review.reviewText}
                    onChange={(e) =>
                      updateReview(review.id, { reviewText: e.target.value })
                    }
                    placeholder={t('settings.googleReviews.reviewTextPlaceholder')}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
                  />
                </div>

                <div>
                  <label className="block text-xs text-gray-600 mb-1">{t('settings.googleReviews.date')}</label>
                  <input
                    type="text"
                    value={review.date}
                    onChange={(e) => updateReview(review.id, { date: e.target.value })}
                    placeholder={t('settings.googleReviews.datePlaceholder')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Auto Play */}
      <div>
        <ToggleSwitch
          label={t('settings.common.autoPlay')}
          checked={component.settings.autoPlay}
          onChange={(checked) => handleUpdate({ autoPlay: checked })}
        />
      </div>

      {component.settings.autoPlay && (
        <div>
          <label className="block text-sm font-medium mb-2">
            {t('settings.googleReviews.autoPlaySpeedSec')}
          </label>
          <input
            type="range"
            min="2000"
            max="10000"
            step="1000"
            value={component.settings.autoPlaySpeed}
            onChange={(e) => handleUpdate({ autoPlaySpeed: parseInt(e.target.value) })}
            className="w-full"
          />
          <div className="text-xs text-gray-400 mt-1">
            {component.settings.autoPlaySpeed / 1000}s
          </div>
        </div>
      )}

      {/* Colors */}
      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.backgroundColor')}</label>
        <ColorPicker
          color={component.settings.backgroundColor}
          onChange={(color) => handleUpdate({ backgroundColor: color })}
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.textColor')}</label>
        <ColorPicker
          color={component.settings.textColor}
          onChange={(color) => handleUpdate({ textColor: color })}
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.googleReviews.cardBackgroundColor')}</label>
        <ColorPicker
          color={component.settings.cardBackgroundColor}
          onChange={(color) => handleUpdate({ cardBackgroundColor: color })}
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.starColor')}</label>
        <ColorPicker
          color={component.settings.starColor}
          onChange={(color) => handleUpdate({ starColor: color })}
        />
      </div>

      {/* Padding */}
      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.padding')}</label>
        <input
          type="range"
          min="0"
          max="80"
          value={component.settings.padding}
          onChange={(e) => handleUpdate({ padding: parseInt(e.target.value) })}
          className="w-full"
        />
        <div className="text-xs text-gray-400 mt-1">
          {component.settings.padding}px
        </div>
      </div>
    </div>
  );
};

